import { PreSpinDropOutGate } from "./PreSpinDropOutGate";
import { PreSpinDropOutPolicy } from "./PreSpinDropOutPolicy";
import { GridPreSpinDrop } from "./GridPreSpinDrop";
import { GridReveal } from "./GridReveal";
import type { Log, PresentationBarrier, PresentationClock, PresentationTaskRunner } from "@slot/presentation";

/**
 * The drop-out at a `spin_boundary_start` (feature spins; base spins in replay): asks the policy,
 * re-arms the turbo slam on the drop-out + reveal scopes for this sub-round, then holds the "spin"
 * barrier and starts the drop-out through the gate without waiting for it. The drop-out releases the
 * barrier when its last symbol starts to fall, so the base layer's reveal prepares while the tail drops
 * (decision 33).
 */
export class PreSpinDropOutBoundaryHook {
  public constructor(
    private readonly deps: {
      /** The "spin" barrier the base layer waits on; the gate's drop-out must release this same one (`dropOutDownAndRecycle({ presentationBarrier })`). */
      barrier?: PresentationBarrier;
      gate: PreSpinDropOutGate;
      policy?: PreSpinDropOutPolicy;
      presentationTaskRunner?: PresentationTaskRunner;
      presentationClock?: PresentationClock;
      log?: Log;
    },
  ) {}

  public onSpinBoundaryStart(payload: { spinModeIdentifier?: string }): void {
    const policy = this.deps.policy ?? new PreSpinDropOutPolicy();
    if (!policy.shouldRunOnSpinBoundaryStart(payload)) return;

    this.deps.log?.(`[PreSpinDropOut] boundary-start mode=${payload.spinModeIdentifier ?? "?"}`);

    // Clear stale slam arms from the previous sub-round (mirrors preSpin): a slam fired after the
    // previous reveal completed must not leak into this sub-round's reveal.
    const runner = this.deps.presentationTaskRunner;
    if (runner) {
      runner.disarmSlamNextRun(GridPreSpinDrop.SCOPE);
      runner.disarmSlamNextRun(GridReveal.SCOPE);
      const mode = this.deps.presentationClock?.getMode();
      if (mode === "turbo" || mode === "super_turbo") {
        runner.armSlamNextRun(GridPreSpinDrop.SCOPE);
        runner.armSlamNextRun(GridReveal.SCOPE);
      }
    }

    // A drop-out already falling (the spin request's) releases the "spin" hold in front of this reveal itself;
    // a second hold here would be a key its one release cannot free.
    if (this.deps.gate.isRunning()) return;
    // The drop-out releases "spin" when its last symbol starts to fall: the reveal prepares while the tail drops.
    this.deps.barrier?.hold("spin");
    void this.deps.gate.request().catch((error: unknown) => {
      this.deps.log?.(`[PreSpinDropOut] drop-out failed: ${error instanceof Error ? error.message : String(error)}`);
    });
  }
}
